import { useEffect, useState } from "react";
import {
  currentDateFormat,
  getLanguage,
  pageIsSearchable,
} from "../Managers/dataProvider";
import {
  EnumMenuType,
  EnumPopupFunctions,
  PopupInfoModel,
  ReportModel,
} from "../Managers/dataStructures";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import {
  dateToString,
  dateToStringForPicker,
  filterReportSearchData,
} from "../Managers/utilityFunctions";
import { fetchBranchListReport } from "../Managers/wepApiManager";
import { getCurrentUser } from "../Managers/profileManager";
import { ActivityIndicator } from "../Managers/utilityComponents";

interface BMWorkstationProps {
  openReportDialog: (item: PopupInfoModel) => void;
  searchString: string;
}

export default function BMWorkstation(props: BMWorkstationProps) {
  const isEnglish = getLanguage();
  const [reports, setReports] = useState<ReportModel[]>([]);
  const [selectedDate, setSelectDate] = useState<Date>(new Date());
  const [activityIndicator, setActivityIndicator] = useState<boolean>(false);

  useEffect(() => {
    setActivityIndicator(true);
    fetchBranchListReport(
      selectedDate,
      getCurrentUser().permanent_branch_id,
      (array_reports: ReportModel[]) => {
        setActivityIndicator(false);
        setReports(array_reports);
      }
    );
  }, [selectedDate]);

  const searchString: string = pageIsSearchable(
    EnumMenuType.BRANCH_MANAGER_WORKSTATION
  )
    ? props.searchString
    : "";

  // const currentDateString: string = dateToString(selectedDate);
  const currentDateStringForPicker: string =
    dateToStringForPicker(selectedDate);

  function onDateChange(x: dayjs.Dayjs | null) {
    //console.log("selected Date=" + x?.format("DD-MMM-YYYY"));
    setSelectDate(x?.toDate() as Date);
  }

  function onReportDetail(report: ReportModel) {
    report.date = selectedDate;
    const item: PopupInfoModel = {
      item: report,
      props: report,
      ftype: EnumPopupFunctions.BM_REPORT_DETAIL,
      callback: () => {},
    };
    props.openReportDialog(item);
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {isEnglish
              ? "Branch Manager Work Station"
              : "Filial-/Schichtleitung Dashboard"}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {isEnglish
              ? "Branch Manager can see list of report of every day. Can see details of every report and approve the reports."
              : "Der Filialleiter oder der Schichtleiter hat die Möglichkeit, die Liste der täglichen Filialberichte einzusehen. Diese enthält die Mitarbeiter, die am gewählten Datum gearbeitet haben, sowie deren Arbeitsbeginn, Arbeitsende und die Gesamtarbeitsstunden. Um die Schicht für den aktuellen Tag zu genehmigen, muss der zuständige Verantwortliche den entsprechenden Bericht aufrufen. Es ist wichtig, dass der Filialleiter oder Schichtleiter die angezeigten Mitarbeiter und deren Arbeitszeiten sorgfältig prüft, bevor die Schicht freigegeben wird."}
          </p>
        </div>
      </div>
      <div className="mt-4">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]}>
            <DatePicker
              label={isEnglish ? "Select Date" : "Datum auswählen"}
              disableFuture
              format={currentDateFormat()}
              onAccept={(x) => onDateChange(x)}
              defaultValue={dayjs(currentDateStringForPicker)}
            />
          </DemoContainer>
        </LocalizationProvider>
      </div>

      <div className="mt-3 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="relative">
              {activityIndicator ? (
                <div className="mt-5 flex items-center justify-center">
                  {ActivityIndicator(1)}
                </div>
              ) : filterReportSearchData(reports, searchString).length === 0 ? (
                <div className="flex mt-10 justify-center font-semibold">
                  {isEnglish
                    ? "No reports available for selected date."
                    : "Für das ausgewählte Datum liegt kein Tagesbericht vor."}
                </div>
              ) : (
                <table className="min-w-full table-fixed divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Branch" : "Filiale"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Date" : "Datum"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Team Lead" : "Teamleiter"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Approved" : "Freigegeben"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Shift" : "Schicht"}
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                      >
                        <span className="sr-only">Download</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filterReportSearchData(reports, searchString).map((x) => [
                      CellReport(x, onReportDetail, selectedDate),
                    ])}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CellReport(
  report: ReportModel,
  handler: (report: ReportModel) => void,
  date_: Date
) {
  const isEnglish = getLanguage();
  function onDetail() {
    handler(report);
  }

  return (
    <tr key={report.report_id} onClick={onDetail}>
      <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
        {getCurrentUser().permanant_branch.name}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {dateToString(date_)}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {report.team_lead_info?.first_name +
          " " +
          report.team_lead_info?.last_name}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {report.shift_plan?.log?.approval_status
          ? isEnglish
            ? "Yes"
            : "freigegeben"
          : isEnglish
          ? "No"
          : "Nicht freigegeben"}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {report.shift_plan?.shift.title}
      </td>
      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
        <a
          onClick={onDetail}
          href={"#" + report.report_id}
          className="text-indigo-600 hover:text-indigo-900"
        >
          {isEnglish ? "Report Detail" : "Filialtagesbericht anzeigen"}
          <span className="sr-only">{", " + report.title}</span>
        </a>
      </td>
    </tr>
  );
}
